// src/components/HeadlineFeed.js
import React, { useEffect, useMemo, useState } from 'react';
import '../styles/HeadlineFeed.css';
import { ART_PHOTO_POOL, REAL_PHOTO_POOL } from './fallbacks';

const LIMIT = 200;
const MIX_RATIO = 0.5; // 50/50 art vs real

// iOS/WKWebView-safe final fallback (tiny gif)
const FINAL_FALLBACK =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
const DEFAULT_HEADLINE_IMG =
  (Array.isArray(ART_PHOTO_POOL) && ART_PHOTO_POOL.find(Boolean)) ||
  (Array.isArray(REAL_PHOTO_POOL) && REAL_PHOTO_POOL.find(Boolean)) ||
  FINAL_FALLBACK;
export default function HeadlineFeed({
  backendBase = '',
  photoPool = ART_PHOTO_POOL,
  realPhotoPool = REAL_PHOTO_POOL,
  onBack = () => window.history.back(),
}) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);



  const fetchNews = async () => {
    setLoading(true);
    try {
      const url = `${backendBase}/api/news?limit=${LIMIT}&maxPerSource=8`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`News fetch failed (${res.status})`);

      const data = await res.json();
      console.log('[HeadlineFeed] /api/news raw:', data);

      const list =
        (Array.isArray(data) && data) ||
        data?.items ||
        data?.articles ||
        data?.data ||
        data?.results ||
        data?.payload ||
        [];

      setArticles(Array.isArray(list) ? list : []);
      // reset failure state on refresh so we try again

    } catch (e) {
      console.error('[HeadlineFeed] news fetch failed', e);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendBase]);

  // Normalize URL so we don't drop everything if backend uses `link`
  const normalizeUrl = (a) => (a?.url || a?.link || a?.href || '').trim();

    const display = useMemo(() => {
      const seen = new Set();

      const deduped = (articles || []).filter((a) => {
        const u = normalizeUrl(a);
        if (!u) return true;
        if (seen.has(u)) return false;
        seen.add(u);
        return true;
      });

      // light shuffle so refresh feels fresh
      return deduped.sort(() => Math.random() - 0.5);
    }, [articles]);

  // Stable hash used to deterministically choose art vs real per headline
  const hash = (str = '') => {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = (h << 5) - h + str.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  };

  // Shuffle pools so the feed feels fresh but still predictable per index
  const [poolOrder, setPoolOrder] = useState([]);
  useEffect(() => {
    const arr = [...(photoPool || [])];
    for (let j = arr.length - 1; j > 0; j--) {
      const k = Math.floor(Math.random() * (j + 1));
      [arr[j], arr[k]] = [arr[k], arr[j]];
    }
    setPoolOrder(arr);
  }, [photoPool]);

  const [realPoolOrder, setRealPoolOrder] = useState([]);
  useEffect(() => {
    const arr = [...(realPhotoPool || [])];
    for (let j = arr.length - 1; j > 0; j--) {
      const k = Math.floor(Math.random() * (j + 1));
      [arr[j], arr[k]] = [arr[k], arr[j]];
    }
    setRealPoolOrder(arr);
  }, [realPhotoPool]);

  const pickFrom = (pool, i) => {
    if (!Array.isArray(pool)) return null;
    const clean = pool
      .map((x) => (typeof x === 'string' ? x.trim() : x))
      .filter(Boolean);
    if (clean.length === 0) return null;
    return clean[i % clean.length];
  };

  const pickArt = (i) => pickFrom(poolOrder.length ? poolOrder : photoPool, i);
  const pickReal = (i) =>
    pickFrom(realPoolOrder.length ? realPoolOrder : realPhotoPool, i);

  // Proxy RSS/news images through backend to avoid hotlink/CORS blocks
  const proxiedNewsImage = (raw) => {
    const u = String(raw || '').trim();
    if (!u) return null;
    const https = u.startsWith('https://')
      ? u
      : u.startsWith('http://')
      ? u.replace('http://', 'https://')
      : '';
    if (!https) return null;
    if (!backendBase) return https; // safe fallback if backendBase not set
    return `${backendBase}/api/news-image?url=${encodeURIComponent(https)}`;
  };

  const initialSrc = (a, i) => {
      const apiSrc = null;

    const art = pickArt(i);
    const real = pickReal(i);

    // Debug: only logs when EVERYTHING is missing/empty
    if (!apiSrc && !art && !real) {
      console.log('⚠️ No image sources for:', {
        title: a?.title,
        url: normalizeUrl(a),
        image: a?.image,
        urlToImage: a?.urlToImage,
        index: i,
      });
    }

    const u = normalizeUrl(a) || `${a?.title || ''}-${i}`;
    const r = (hash(u) % 100) / 100;
    const wantsArt = r < MIX_RATIO;

    const candidates = wantsArt ? [art, apiSrc, real] : [apiSrc, real, art];

    for (const c of candidates) {
      if (typeof c === 'string' && c.length > 5) return c;
    }

    return FINAL_FALLBACK;
  };

  return (
    <div className="headline-feed">
      <div className="headline-controls">
        <button className="headline-back" onClick={onBack}>
          ← Back
        </button>

        <button
          className={`headline-refresh ${loading ? 'is-loading' : ''}`}
          onClick={fetchNews}
          disabled={loading}
          aria-label="Refresh"
          title="Refresh"
        >
          <span className="icon">⟳</span>
        </button>
      </div>

      <div className="headline-list">
        {display.length === 0 && !loading && (
          <p className="headline-error">No headlines available.</p>
        )}

          {display.map((a, i) => {
            const u = normalizeUrl(a);
            const key = u || `${a?.title || 'headline'}-${i}`;

            return (
              <div className="headline-card" key={key}>
                <div className="thumb">
                  <img
                    className="img"
                    src={initialSrc(a, i) || DEFAULT_HEADLINE_IMG}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      const el = e.currentTarget;

                      console.log('FAILED HEADLINE IMAGE:', el.src);

                      if (el.dataset.fallback === '1') return;

                      el.dataset.fallback = '1';
                      el.src = DEFAULT_HEADLINE_IMG;
                    }}                  />
                </div>

                <div className="headline-content">
                  {u ? (
                    <a href={u} target="_blank" rel="noopener noreferrer">
                      <h3>{a.title}</h3>
                    </a>
                  ) : (
                    <h3>{a.title}</h3>
                  )}
                  {a.source && <p className="source">{a.source}</p>}
                    </div>
                               </div>
                             );
                           })}
                         </div>
                       </div>
                     );
                   }
